import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Phone, ArrowRight, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { teleproApi } from "../lib/teleproApi";

/**
 * /teleprospecteur/inscription — self-service signup form.
 *
 * Auto-activates the account (per product decision 1a). After success the user
 * is shown a "check your inbox" panel — the welcome email contains a magic link
 * that bridges them straight into the portal.
 */
export default function TeleproSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "France",
    iban: "",
    bic_swift: "",
    pro_email: "",
    siret_status: "has_siret",           // "has_siret" | "pending"
    business_id_type: "fr_siret",        // "fr_siret" | "eu_vat" | "national"
    siret_number: "",
    accept_terms: false,
  });
  const [proEmailTouched, setProEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [doneCode, setDoneCode] = useState(null);
  const [doneLink, setDoneLink] = useState(null);

  const set = (k, v) => setForm((p) => {
    const next = { ...p, [k]: v };
    // Auto-suggest pro Gmail from the first_name as the telepro types it
    if (k === "first_name" && !proEmailTouched) {
      const clean = v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
      next.pro_email = clean ? `${clean}-moncommerce68@gmail.com` : "";
    }
    return next;
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) {
      toast.error("Tu dois accepter les conditions de partenariat.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await teleproApi.post("/signup", {
        ...form,
        iban: form.iban.toUpperCase().replace(/\s/g, ""),
        bic_swift: form.bic_swift.toUpperCase().replace(/\s/g, ""),
      });
      setDoneCode(res.data.code);
      setDoneLink(res.data.link || null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) toast.error("Cet email est déjà enregistré. Connecte-toi via la page de connexion.");
      else if (status === 429) toast.error("Trop d'inscriptions depuis cette adresse. Réessaie demain.");
      else if (Array.isArray(detail)) {
        // Pydantic renvoie une liste d'erreurs — on traduit + indique le champ concerné.
        const first = detail[0] || {};
        const fieldMap = {
          first_name: "Prénom", last_name: "Nom",
          email: "Email", phone: "Téléphone",
          address: "Adresse", postal_code: "Code postal", city: "Ville",
          country: "Pays", iban: "IBAN", bic_swift: "BIC/SWIFT",
          pro_email: "Email pro", siret_number: "Identifiant entreprise",
          business_id_type: "Type d'identifiant",
          siret_status: "Statut professionnel", accept_terms: "Conditions",
        };
        const rawField = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : null;
        const fieldLabel = fieldMap[rawField] || rawField || "Un champ";
        const msg = String(first.msg || "").toLowerCase();
        let human = first.msg || "Formulaire invalide";
        if (msg.includes("at least") && msg.includes("character")) {
          const m = msg.match(/at least (\d+)/);
          const n = m ? m[1] : "quelques";
          human = `${fieldLabel} : minimum ${n} caractères`;
        } else if (msg.includes("at most") && msg.includes("character")) {
          const m = msg.match(/at most (\d+)/);
          const n = m ? m[1] : "quelques";
          human = `${fieldLabel} : maximum ${n} caractères`;
        } else if (msg.includes("valid email") || msg.includes("email address")) {
          human = `${fieldLabel} : format email invalide`;
        } else if (msg.includes("field required") || msg.includes("missing")) {
          human = `${fieldLabel} : ce champ est obligatoire`;
        } else if (rawField) {
          human = `${fieldLabel} : ${first.msg}`;
        }
        toast.error(human);
      }
      else toast.error(detail || "Erreur. Vérifie tes informations.");
    } finally {
      setSubmitting(false);
    }
  };

  if (doneCode) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 py-12">
        <Helmet><title>Bienvenue dans l&apos;équipe — MonCommerce68</title><meta name="robots" content="noindex,nofollow"/></Helmet>
        <div className="w-full max-w-md bg-card border border-primary/40 rounded-sm p-6 sm:p-8 text-center" data-testid="telepro-signup-success">
          <CheckCircle size={36} weight="duotone" className="text-primary mx-auto mb-3"/>
          <div className="label-uppercase mb-1">Compte créé</div>
          <h1 className="font-display text-2xl font-medium leading-tight mb-3">Bienvenue dans l&apos;équipe !</h1>
          <p className="text-sm text-muted-foreground mb-2">Ton code partenaire :</p>
          <div className="bg-primary/10 text-primary font-mono text-2xl font-bold tracking-wider py-3 rounded-sm mb-5" data-testid="signup-code">{doneCode}</div>

          {doneLink ? (
            <>
              <p className="text-sm leading-relaxed mb-3">
                <b>Accède à ton portail maintenant</b> en cliquant ci-dessous (lien valable 30 minutes) :
              </p>
              <a
                href={doneLink}
                className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-3 rounded-sm text-sm font-bold inline-flex items-center justify-center gap-2 mb-3"
                data-testid="signup-direct-link"
              >
                Accéder à mon portail <ArrowRight size={14} weight="bold"/>
              </a>
              <div className="bg-muted/40 border border-border rounded-sm p-3 text-left mb-4" data-testid="signup-link-fallback">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">📌 Garde ce lien (au cas où)</div>
                <p className="text-[11px] font-mono break-all leading-relaxed text-foreground/80 select-all">{doneLink}</p>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                On t&apos;envoie aussi ce lien par email. Si tu ne le reçois pas (vérifie tes <b>spams</b> aussi),
                utilise le bouton ci-dessus — ça marche aussi bien.
              </p>
            </>
          ) : (
            <p className="text-sm leading-relaxed mb-4">
              On vient de t&apos;envoyer un email avec ton lien d&apos;accès au portail. <b>Vérifie ta boîte (et les spams).</b> Le lien arrive dans 1-2 minutes.
            </p>
          )}

          <button
            onClick={() => navigate("/teleprospecteur", { replace: true })}
            className="w-full bg-card text-foreground border border-border hover:bg-accent px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2"
            data-testid="signup-go-login"
          >
            Aller à la page de connexion <ArrowRight size={14} weight="bold"/>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <Helmet><title>Devenir téléprospecteur — MonCommerce68</title><meta name="robots" content="noindex,nofollow"/></Helmet>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-3">
          <Phone size={20} weight="duotone"/>
        </div>
        <div className="label-uppercase mb-1">Rejoins l&apos;équipe Téléprospection</div>
        <h1 className="font-display text-3xl font-medium leading-tight mb-2">Inscription</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Commissions à la vente + récurrentes. Ton compte est actif immédiatement après inscription —
          tu reçois ta liste d&apos;appels du jour dès le lendemain matin.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4 bg-card border border-border rounded-sm p-6" data-testid="telepro-signup-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Prénom" required>
            <input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} maxLength={60} className={inputCls} data-testid="signup-first-name"/>
          </Field>
          <Field label="Nom" required>
            <input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} maxLength={60} className={inputCls} data-testid="signup-last-name"/>
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Email" required>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="signup-email"/>
          </Field>
          <Field label="Téléphone" required>
            <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78" className={inputCls} data-testid="signup-phone"/>
          </Field>
        </div>
        <div className="bg-secondary/5 border border-secondary/30 rounded-sm p-3" data-testid="signup-pro-email-block">
          <Field
            label="Gmail pro à créer"
            hint="Pour contacter les commerçants qui n'ont qu'un email — le Gmail moncommerce68@gmail.com est réservé au créateur"
          >
            <input
              type="email"
              value={form.pro_email}
              onChange={(e) => { setProEmailTouched(true); set("pro_email", e.target.value); }}
              placeholder="prenom-moncommerce68@gmail.com"
              maxLength={120}
              className={inputCls + " font-mono"}
              data-testid="signup-pro-email"
              autoComplete="off"
            />
          </Field>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
            <b className="text-foreground">Comment faire ?</b> Va sur <a href="https://accounts.google.com/signup" target="_blank" rel="noopener noreferrer" className="underline text-primary">accounts.google.com/signup</a>,
            crée un compte avec ton prénom + suffixe <code className="bg-card px-1 rounded">-moncommerce68</code> (suggestion pré-remplie ci-dessus,
            modifie-la si elle est prise). Tu pourras le compléter plus tard depuis ton portail.
          </p>
        </div>
        <Field label="Adresse postale" required hint="Pour les feuilles de paie">
          <input required value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="12 rue des Lilas" className={inputCls} data-testid="signup-address"/>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Code postal" required>
            <input required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="68000" maxLength={12} className={inputCls} data-testid="signup-postal"/>
          </Field>
          <div className="col-span-2">
            <Field label="Ville" required>
              <input required value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Colmar" className={inputCls} data-testid="signup-city"/>
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Pays" required>
              <input required value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="France, Belgique, Suisse, Maroc…" maxLength={60} className={inputCls} data-testid="signup-country" autoComplete="country-name"/>
            </Field>
          </div>
        </div>

        {/* Bloc coordonnées bancaires — international */}
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 space-y-3" data-testid="signup-bank-block">
          <div className="text-xs font-bold uppercase tracking-wider text-primary">
            Coordonnées bancaires <span className="text-secondary">*</span>
          </div>
          <Field label="IBAN" required hint="Tous les IBAN internationaux acceptés — France (FR76…), Belgique (BE68…), Suisse (CH93…), Allemagne (DE89…), Espagne (ES76…), Portugal (PT50…), Luxembourg (LU28…), Maroc (MA64…), Tunisie (TN59…), Sénégal (SN12…), Mali (ML13…), Côte d'Ivoire (CI93…), Burkina Faso (BF42…), Togo (TG53…), Bénin (BJ66…), Algérie (DZ58…), Cameroun (CM21…), etc.">
            <input
              required
              value={form.iban}
              onChange={(e) => set("iban", e.target.value.toUpperCase())}
              placeholder="Ex : FR76 1234… ou SN12 K001 0015 2000…"
              maxLength={40}
              className={inputCls + " font-mono uppercase"}
              data-testid="signup-iban"
              autoComplete="off"
              inputMode="text"
              spellCheck={false}
            />
          </Field>
          <Field label="BIC / SWIFT" hint="Optionnel pour la France (SEPA) — obligatoire hors zone euro. 8 ou 11 caractères (ex : BNPAFRPPXXX, RBOSGB2L).">
            <input
              value={form.bic_swift}
              onChange={(e) => set("bic_swift", e.target.value.toUpperCase())}
              placeholder="Ex : BNPAFRPPXXX (facultatif si IBAN européen)"
              maxLength={11}
              className={inputCls + " font-mono uppercase"}
              data-testid="signup-bic"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        </div>

        {/* Statut professionnel — support international (2026-02) */}
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-3" data-testid="signup-siret-block">
          <div className="text-xs font-bold uppercase tracking-wider text-primary mb-2">
            Statut professionnel <span className="text-secondary">*</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-xs cursor-pointer ${form.siret_status === "has_siret" ? "border-primary bg-primary/10" : "border-border"}`}>
              <input
                type="radio"
                name="siret_status"
                value="has_siret"
                checked={form.siret_status === "has_siret"}
                onChange={() => set("siret_status", "has_siret")}
                data-testid="signup-siret-status-has"
              />
              <span>J&apos;ai déjà mon identifiant</span>
            </label>
            <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-xs cursor-pointer ${form.siret_status === "pending" ? "border-primary bg-primary/10" : "border-border"}`}>
              <input
                type="radio"
                name="siret_status"
                value="pending"
                checked={form.siret_status === "pending"}
                onChange={() => set("siret_status", "pending")}
                data-testid="signup-siret-status-pending"
              />
              <span>Immatriculation en cours</span>
            </label>
          </div>

          {form.siret_status === "has_siret" && (
            <>
              {/* Sélecteur de type d'identifiant */}
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
                  Type d&apos;identifiant
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { v: "fr_siret", label: "SIRET France", hint: "14 chiffres" },
                    { v: "eu_vat", label: "TVA UE (VAT)", hint: "BE, DE, IT…" },
                    { v: "national", label: "Autre pays", hint: "UK, CH, MA, US…" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`flex flex-col items-start gap-0.5 border rounded-sm px-2.5 py-2 text-xs cursor-pointer ${
                        form.business_id_type === opt.v
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <input
                          type="radio"
                          name="business_id_type"
                          value={opt.v}
                          checked={form.business_id_type === opt.v}
                          onChange={() => set("business_id_type", opt.v)}
                          data-testid={`signup-business-id-type-${opt.v}`}
                        />
                        <span className="font-semibold">{opt.label}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground ml-5">{opt.hint}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Champ identifiant adapté */}
              {form.business_id_type === "fr_siret" && (
                <Field label="Numéro SIRET" required hint="14 chiffres (ou 9 pour le SIREN)">
                  <input
                    required
                    value={form.siret_number}
                    onChange={(e) => set("siret_number", e.target.value.replace(/\D/g, "").slice(0, 14))}
                    placeholder="12345678901234"
                    inputMode="numeric"
                    className={inputCls + " font-mono"}
                    data-testid="signup-siret-number"
                  />
                </Field>
              )}

              {form.business_id_type === "eu_vat" && (
                <Field label="Numéro de TVA intracommunautaire" required hint="Ex : BE0123456789 (Belgique), DE123456789 (Allemagne), IT12345678901 (Italie), LU12345678, ES-A12345678, PT501234567…">
                  <input
                    required
                    value={form.siret_number}
                    onChange={(e) => set("siret_number", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15))}
                    placeholder="Ex : BE0123456789"
                    className={inputCls + " font-mono uppercase"}
                    data-testid="signup-siret-number"
                    spellCheck={false}
                  />
                </Field>
              )}

              {form.business_id_type === "national" && (
                <Field label="Identifiant d'entreprise" required hint="Companies House UK (12345678), UID Suisse (CHE-123.456.789), Kbis DE (HRB12345), RC Maroc, Codice Fiscale IT, EIN USA (12-3456789)…">
                  <input
                    required
                    value={form.siret_number}
                    onChange={(e) => set("siret_number", e.target.value.toUpperCase().replace(/[^A-Z0-9\-\.]/g, "").slice(0, 30))}
                    placeholder="Ex : CHE-123.456.789 (Suisse)"
                    className={inputCls + " font-mono uppercase"}
                    data-testid="signup-siret-number"
                    spellCheck={false}
                    maxLength={30}
                  />
                </Field>
              )}
            </>
          )}

          {form.siret_status === "pending" && (
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Tu pourras signer ton contrat sans identifiant d&apos;entreprise. En cochant les cases obligatoires,
              tu t&apos;engages à nous le transmettre dès son attribution (depuis ton portail, section « Mon compte »).
            </p>
          )}
        </div>
        <label className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-sm">
          <input
            type="checkbox"
            checked={form.accept_terms}
            onChange={(e) => set("accept_terms", e.target.checked)}
            className="mt-0.5"
            data-testid="signup-accept"
            required
          />
          <span>
            J&apos;accepte les <b>conditions de partenariat</b> : je m&apos;engage à respecter les règles éthiques
            (pas de pression, pas de mensonge sur la nature du service, respect des refus). En cas de manquement,
            mon compte peut être désactivé sans préavis. Mes données sont traitées dans le respect du RGPD —
            voir <Link to="/legal" className="underline">conditions complètes</Link>.
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting || !form.accept_terms}
          className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-3 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="signup-submit"
        >
          {submitting ? "Création du compte…" : <>Créer mon compte <ArrowRight size={14} weight="bold"/></>}
        </button>
        <div className="text-xs text-muted-foreground flex items-start gap-2 pt-2 border-t border-border">
          <ShieldCheck size={14} weight="duotone" className="text-primary shrink-0 mt-0.5"/>
          <span>Tes informations bancaires sont chiffrées et stockées chez nous uniquement pour les virements de commissions. Aucun tiers n&apos;y a accès.</span>
        </div>
      </form>
      <div className="mt-6 text-center text-sm">
        <span className="text-muted-foreground">Déjà inscrit ? </span>
        <Link to="/teleprospecteur" className="text-primary font-semibold hover:underline" data-testid="signup-login-link">
          Connecte-toi ici
        </Link>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-border rounded-sm px-3 py-2 text-sm bg-background focus:border-primary outline-none";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
        {label}{required && <span className="text-secondary"> *</span>}
        {hint && <span className="font-normal normal-case tracking-normal text-[10px] text-muted-foreground/80 ml-1">— {hint}</span>}
      </span>
      {children}
    </label>
  );
}
